// Library Methods
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import ComparisonImageSlider from "../ComparisonImageSlider";

// Types
import { IndividualExternalExamType } from "src/services/exams/types";

const OVERVIEW_WIDTH = "180px";

type OCTComparisonImageType = {
  images: IndividualExternalExamType["images"];
  // Used only when `images` is empty, so exams predating the images array
  // (or ones where it hasn't been populated yet) still show something.
  fallbackImageUrl?: string | null;
  overviewWidth?: string;
  fillOverviewHeight?: boolean;
  compactSlider?: boolean;
};

const OCTComparisonImage = ({
  images,
  fallbackImageUrl,
  overviewWidth = OVERVIEW_WIDTH,
  fillOverviewHeight = false,
  compactSlider = false,
}: OCTComparisonImageType) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [selectedImageId, setSelectedImageId] = useState(() => images[0]?.id);

  // Keep the selection in sync if the images array itself changes (e.g. a
  // different exam is now being shown through the same mounted component).
  useEffect(() => {
    if (!images.some((image) => image.id === selectedImageId)) {
      setSelectedImageId(images[0]?.id);
    }
  }, [images, selectedImageId]);

  const imageUrl = useMemo(
    () =>
      images.find((image) => image.id === selectedImageId)?.imageUrl ??
      fallbackImageUrl ??
      "",
    [images, selectedImageId, fallbackImageUrl]
  );

  // Scrubbing the slider swaps `imageUrl` to whichever frame the user drags
  // to, which would otherwise block on a fresh network fetch for any frame
  // not visited yet. Warming the browser's cache for the whole sequence up
  // front means every frame swap after that is instant.
  useEffect(() => {
    const preloadedImages = images.map((image) => {
      const preloadedImage = new Image();
      preloadedImage.src = image.imageUrl;
      return preloadedImage;
    });

    return () => {
      preloadedImages.forEach((preloadedImage) => {
        preloadedImage.src = "";
      });
    };
  }, [images]);

  // Marquee position/height as fractions (0-1) of the detail column's full
  // scrollable content, applied directly as % on the overview image -- no
  // pixel math needed since both are the same fraction of the same image.
  const [marquee, setMarquee] = useState({ top: 0, height: 1 });

  const updateMarquee = useCallback(() => {
    const el = scrollRef.current;
    if (!el || el.scrollHeight === 0) return;

    setMarquee({
      top: el.scrollTop / el.scrollHeight,
      height: Math.min(1, el.clientHeight / el.scrollHeight),
    });
  }, []);

  useEffect(() => {
    updateMarquee();
    window.addEventListener("resize", updateMarquee);
    return () => window.removeEventListener("resize", updateMarquee);
  }, [updateMarquee]);

  // Jump back to the top of the new slice instead of keeping whatever
  // scroll offset the previous one was at.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [imageUrl]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        backgroundColor: "#000",
      }}
    >
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: fillOverviewHeight ? "stretch" : "flex-start",
            p: "8px",
            flexShrink: 0,
            width: fillOverviewHeight ? "auto" : overviewWidth,
            maxWidth: fillOverviewHeight ? overviewWidth : undefined,
            height: fillOverviewHeight ? "fit-content" : "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={
              fillOverviewHeight
                ? {
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    height: "100%",
                  }
                : { position: "relative", width: "100%" }
            }
          >
            <img
              src={imageUrl}
              alt={t("OCT overview")}
              style={
                fillOverviewHeight
                  ? {
                      height: "100%",
                      width: "auto",
                      display: "block",
                      objectFit: "contain",
                    }
                  : { width: "100%", height: "auto", display: "block" }
              }
            />
            <Box
              sx={{
                position: "absolute",
                left: 0,
                width: "100%",
                top: `${marquee.top * 100}%`,
                height: `${marquee.height * 100}%`,
                border: "2px solid #E2772E",
                boxSizing: "border-box",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Box>

        {/* Detail column: a large, scrollable rendition of the same image */}
        <Box
          ref={scrollRef}
          onScroll={updateMarquee}
          sx={{ flex: 1, minHeight: 0, height: "100%", overflowY: "auto" }}
        >
          <img
            src={imageUrl}
            alt={t("OCT detail")}
            onLoad={updateMarquee}
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </Box>
      </Box>

      {images.length > 1 && (
        <ComparisonImageSlider
          images={images}
          selectedImageId={selectedImageId}
          onSelect={(image) => setSelectedImageId(image.id)}
          compact={compactSlider}
        />
      )}
    </Box>
  );
};

export default OCTComparisonImage;
